import type { AlertingService } from '@inato/browser-tracking';
import { Effect, ParseResult, Schema, pipe, Either, Data } from 'effect';

import {
  TrialInformationFetchService,
  type TrialInformationWithEligibilityCriteria,
} from '../../application/TrialInformationFetchService';
import { fetchWithRetry } from '../shared';
import { formatIECriteria } from './formatIECriteria';
import {
  apiResponseSchemaForExternalTrialCreation,
  apiResponseSchemaForTrialExperience,
  phaseMap,
} from './schema';

// the reality is a bit more but I could not find the exact value in the document
// and 1000 is already too much to find relevant studies
const MAX_NUMBER_OF_CHARS_FOR_SEARCH = 1000;

const buildClinicalTrialsUrl = (studyId: string): URL => {
  const studyIdWithEscapedParenthesis = studyId
    .replaceAll('(', '\\(')
    .replaceAll(')', '\\)');
  const url = new URL('/api/v2/studies', 'https://clinicaltrials.gov');
  url.searchParams.set('query.id', studyIdWithEscapedParenthesis);
  return url;
};

export const buildClinicalTrialsUrlForTrialExperience = (studyId: string) =>
  pipe(buildClinicalTrialsUrl(studyId), addSearchParamsForTrialExperience);

const addSearchParamsForTrialExperience = (url: URL): string => {
  url.searchParams.set(
    'fields',
    'NCTId,LeadSponsorName,OfficialTitle,Phase,Condition',
  );
  return url.toString();
};
const addSearchParamsForExternalTrialCreation = (url: URL): string => {
  url.searchParams.set(
    'fields',
    'NCTId,LeadSponsorName,OfficialTitle,EligibilityCriteria,Sex,MinimumAge',
  );
  return url.toString();
};

const buildClinicalTrialsUrlForExternalTrialCreation = (studyId: string) =>
  pipe(
    buildClinicalTrialsUrl(studyId),
    addSearchParamsForExternalTrialCreation,
  );

interface ErrorWithContext {
  error: Error;
  context: { responseBody?: string };
}
const parseResponse = <A, I>({
  response,
  responseText,
  apiResponseSchema,
}: {
  response: Response;
  responseText: string;
  apiResponseSchema: Schema.Schema<A, I>;
}) => {
  if (!response.ok) {
    return Effect.fail({
      error: new Error(responseText),
      context: {},
    } as ErrorWithContext);
  }
  return pipe(
    responseText,
    Schema.decode(Schema.parseJson(apiResponseSchema)),
    Effect.mapError(error => ({
      error: new Error(ParseResult.TreeFormatter.formatErrorSync(error)),
      context: {
        responseBody: responseText,
      },
    })),
  );
};

const isValidStudyIdForSearch = (studyId: string | null): studyId is string => {
  return (
    !!studyId &&
    studyId.length <= MAX_NUMBER_OF_CHARS_FOR_SEARCH &&
    !studyId.trim().includes(' ')
  );
};

const fetchStudies = <A, I>({
  url,
  apiResponseSchema,
}: {
  url: string;
  apiResponseSchema: Schema.Schema<A, I>;
}) =>
  pipe(
    Effect.tryPromise<Response, ErrorWithContext>({
      try: () =>
        fetchWithRetry({
          url,
          retries: 1,
        }),
      catch: error => ({
        error:
          error instanceof Error
            ? error
            : new Error((error as any).toString() as string),
        context: {},
      }),
    }),
    Effect.flatMap(response =>
      pipe(
        Effect.promise(() => response.text()),
        Effect.map(responseText => ({ responseText, response })),
      ),
    ),
    Effect.flatMap(({ response, responseText }) =>
      parseResponse({
        response,
        responseText,
        apiResponseSchema,
      }),
    ),
  );

export const getClinicalTrialsForTrialExperience = (studyId: string | null) => {
  if (!isValidStudyIdForSearch(studyId)) {
    return Effect.succeed(
      TrialInformationFetchService.emptyTrialInformationWithDiseaseAreas(),
    );
  }
  return pipe(
    fetchStudies({
      url: buildClinicalTrialsUrlForTrialExperience(studyId),
      apiResponseSchema: apiResponseSchemaForTrialExperience,
    }),
    Effect.map(studies => {
      if (!studies.studies[0]) {
        return TrialInformationFetchService.emptyTrialInformationWithDiseaseAreas();
      }

      const firstTrial = studies.studies[0];

      const formattedPhases =
        firstTrial.protocolSection.designModule?.phases?.map(
          phase => phaseMap[phase],
        );

      return {
        nctId: firstTrial.protocolSection.identificationModule?.nctId ?? '',
        phase: formattedPhases?.join(', ') ?? '',
        sponsor:
          firstTrial.protocolSection.sponsorCollaboratorsModule?.leadSponsor
            ?.name ?? '',
        diseaseAreas:
          firstTrial.protocolSection.conditionsModule?.conditions || [],
        title:
          firstTrial.protocolSection.identificationModule?.officialTitle ?? '',
      };
    }),
  );
};

export class CriteriaError extends Data.TaggedError('CriteriaError')<{
  raw: string;
  trialInformation: TrialInformationWithEligibilityCriteria;
}> {}

export const getClinicalTrialsForExternalTrialCreation = (
  studyId: string | null,
) =>
  Effect.gen(function* () {
    if (!isValidStudyIdForSearch(studyId)) {
      return TrialInformationFetchService.emptyTrialInformationWithEligibilityCriteria();
    }

    const studies = yield* fetchStudies({
      url: buildClinicalTrialsUrlForExternalTrialCreation(studyId),
      apiResponseSchema: apiResponseSchemaForExternalTrialCreation,
    });

    if (!studies.studies[0]) {
      return TrialInformationFetchService.emptyTrialInformationWithEligibilityCriteria();
    }

    const firstTrial = studies.studies[0];
    const criteria = formatIECriteria({
      eligibilityCriteria:
        firstTrial.protocolSection.eligibilityModule?.eligibilityCriteria ?? '',
      minimumAge:
        firstTrial.protocolSection.eligibilityModule?.minimumAge ?? '',
      sex: firstTrial.protocolSection.eligibilityModule?.sex ?? '',
    });

    const mapTrialInformation = (criteria: {
      inclusionCriteria: string;
      exclusionCriteria: string;
    }) => ({
      nctId: firstTrial.protocolSection.identificationModule?.nctId ?? '',
      sponsor:
        firstTrial.protocolSection.sponsorCollaboratorsModule?.leadSponsor
          ?.name ?? '',
      title:
        firstTrial.protocolSection.identificationModule?.officialTitle ?? '',
      inclusionCriteria: criteria.inclusionCriteria,
      exclusionCriteria: criteria.exclusionCriteria,
    });

    if (Either.isLeft(criteria)) {
      return yield* Effect.fail(
        new CriteriaError({
          trialInformation: mapTrialInformation({
            inclusionCriteria: criteria.left.inclusionCriteria,
            exclusionCriteria: criteria.left.exclusionCriteria,
          }),
          raw: criteria.left.raw,
        }),
      );
    }

    return mapTrialInformation({
      inclusionCriteria: criteria.right.inclusionCriteria,
      exclusionCriteria: criteria.right.exclusionCriteria,
    });
  });

export const buildClinicalTrialsTrialInformationFetchService = ({
  alertingService,
}: {
  alertingService: AlertingService;
}): TrialInformationFetchService.TrialInformationFetchService => {
  return {
    getTrialInformationForTrialExperience: async (studyId: string | null) =>
      pipe(
        getClinicalTrialsForTrialExperience(studyId),
        Effect.catchAll(({ error, context }) => {
          alertingService.reportError(
            new Error(
              `Could not get trial information from clinicaltrials.gov: ${error.message}`,
            ),
            { ...context, studyId },
          );
          return Effect.succeed(
            TrialInformationFetchService.emptyTrialInformationWithDiseaseAreas(),
          );
        }),
        Effect.runPromise,
      ),
    getTrialInformationForExternalTrialCreation: async (
      studyId: string | null,
    ) =>
      pipe(
        getClinicalTrialsForExternalTrialCreation(studyId),
        Effect.catchTag('CriteriaError', error => {
          alertingService.reportError(
            new Error(`'No eligibility criteria found'`),
            { studyId, raw: error.raw },
          );

          return Effect.succeed(error.trialInformation);
        }),
        Effect.catchAll(({ error, context }) => {
          alertingService.reportError(
            new Error(
              `Could not get trial information for external trial creation from clinicaltrials.gov: ${error.message}`,
            ),
            { ...context, studyId },
          );
          return Effect.succeed(
            TrialInformationFetchService.emptyTrialInformationWithEligibilityCriteria(),
          );
        }),
        Effect.runPromise,
      ),
  };
};
